import type { AircraftClassId } from './airlines';
import { BuildingType, type BuildingView, dollars } from './buildings';
import { crewedVehicles, GROUND_CONTRACT_BELOW } from './staff';

/*
 * Ground handling: the vehicles that serve aircraft at their stands. With its own ground
 * handling base the airport has a fleet limited by the base level: services are quick and free,
 * but when every vehicle of a kind is busy, the next aircraft waits. Without a base an outside
 * contractor does the work, slower and for a fee. Fuel trucks belong to the fuel depot.
 *
 * Which vehicles an aircraft holds is one bit mask per aircraft (`Aircraft.gse`); how many are
 * in use is derived from those masks, so allocations can never leak.
 */

/** Ground support equipment kinds; bit i of a mask is kind i. Saved: append only. */
export const Gse = {
  Stairs: 0,
  Bus: 1,
  Baggage: 2,
  Catering: 3,
  Cleaning: 4,
  Fuel: 5,
  Tug: 6,
  Deicer: 7,
} as const;
export type GseId = (typeof Gse)[keyof typeof Gse];
export const GSE_COUNT = 8;
export const GSE_KINDS: readonly GseId[] = [0, 1, 2, 3, 4, 5, 6, 7];

export const gseBit = (kind: GseId): number => 1 << kind;
const ALL = (1 << GSE_COUNT) - 1;
export const REMOTE_DOOR_VEHICLES = gseBit(Gse.Stairs) | gseBit(Gse.Bus);

/**
 * Vehicles of each kind (stairs, bus, baggage, catering, cleaning, fuel, tug, de-icer) per base
 * level. De-icing trucks come from level 2.
 */
const BASE_FLEET: readonly (readonly number[])[] = [
  [0, 0, 0, 0, 0, 0, 0, 0],
  [2, 1, 2, 1, 1, 0, 1, 0],
  [3, 2, 4, 2, 2, 0, 2, 1],
  [5, 3, 6, 3, 3, 0, 3, 2],
  [7, 5, 9, 5, 5, 0, 5, 3],
  // Levels 5 and 6 (tower Lv5): the fleet of a hub.
  [9, 6, 12, 6, 6, 0, 6, 4],
  [11, 8, 15, 8, 8, 0, 8, 5],
];
/**
 * Turnarounds a base of `level` serves at once with its own vehicles (a set each: its catering
 * trucks are the scarcest), for the player's estimates of what their traffic needs.
 */
export function baseTurnarounds(level: number): number {
  return BASE_FLEET[Math.min(Math.max(level, 0), BASE_FLEET.length - 1)]?.[Gse.Catering] ?? 0;
}
/** The highest ground handling base level. */
export const BASE_MAX_LEVEL = BASE_FLEET.length - 1;

/** Fuel trucks per fuel depot level. */
const DEPOT_TRUCKS: readonly number[] = [0, 2, 3, 5];

/** Fee per turnaround done by the outside contractor (cents). */
export const HANDLING_FEE: Readonly<Record<AircraftClassId, number>> = {
  1: dollars(250),
  2: dollars(500),
  3: dollars(1_200),
  4: dollars(80),
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: dollars(40),
  10: dollars(2_000),
  11: dollars(2_200),
};
/** De-icing by an outside contractor when the airport has no de-icing truck (cents). */
export const DEICE_FEE: Readonly<Record<AircraftClassId, number>> = {
  1: dollars(300),
  2: dollars(600),
  3: dollars(1_500),
  4: dollars(120),
  5: dollars(300),
  6: dollars(800),
  7: dollars(100),
  8: 0,
  9: dollars(60),
  10: dollars(2_400),
  11: dollars(2_600),
};
/** The contractor takes this much longer (percent) for baggage, cleaning and catering. */
export const CONTRACTOR_TIME_PERCENT = 150;
/** A de-icing pad's fixed gantries take this much of a truck's time (percent). */
export const PAD_TIME_PERCENT = 60;
/** Pushback ends (the tug is released) this many tiles along the taxi-out route. */
export const PUSHBACK_TILES = 3;

export interface Fleet {
  /** Vehicles owned, per kind. */
  readonly total: number[];
  /** Vehicles in use, per kind. */
  readonly used: number[];
  /** No ground handling base yet: a contractor serves (without limits) instead. */
  readonly contracted: boolean;
}

/** The airport's fleet and its use by the given aircraft. */
export function groundFleet(
  buildings: readonly BuildingView[],
  aircraft: readonly { readonly gse: number }[],
  /** Ground crews on duty, per mille of what the fleet needs (see staff.ts). */
  crews = 1000,
): Fleet {
  const total = new Array<number>(GSE_COUNT).fill(0);
  let bases = 0;
  for (const b of buildings) {
    if (b.type === BuildingType.GroundHandling && b.level >= 1) {
      bases++;
      const row = BASE_FLEET[Math.min(b.level, BASE_FLEET.length - 1)] ?? [];
      for (let k = 0; k < GSE_COUNT; k++) total[k] = (total[k] ?? 0) + (row[k] ?? 0);
    }
    if (b.type === BuildingType.FuelDepot && b.level >= 1) {
      total[Gse.Fuel] = (total[Gse.Fuel] ?? 0) + (DEPOT_TRUCKS[b.level] ?? 0);
    }
  }
  const used = new Array<number>(GSE_COUNT).fill(0);
  for (const a of aircraft) {
    for (let k = 0; k < GSE_COUNT; k++) if (a.gse & (1 << k)) used[k] = (used[k] ?? 0) + 1;
  }
  // Vehicles need drivers: a short-staffed base runs part of its fleet, and with hardly any
  // crews left the outside contractor takes over.
  const contracted = bases === 0 || crews < GROUND_CONTRACT_BELOW;
  for (let k = 0; k < GSE_COUNT; k++) total[k] = crewedVehicles(total[k] ?? 0, crews);
  return { total, used, contracted };
}

/**
 * The part of `mask` the airport must supply itself: everything with its own base, only fuel
 * trucks with a contractor (whose vehicles are not counted).
 */
export function ownVehicles(fleet: Fleet, mask: number): number {
  return mask & (fleet.contracted ? gseBit(Gse.Fuel) : ALL);
}

/** Takes the vehicles in `mask` the aircraft does not hold yet: all of them, or none. */
export function acquire(
  fleet: Fleet,
  a: { gse: number; gseEta?: number[] },
  mask: number,
  /** Ticks each newly taken vehicle needs to drive to the aircraft. */
  travel?: (kind: number) => number,
): boolean {
  const missing = mask & ~a.gse;
  for (let k = 0; k < GSE_COUNT; k++) {
    if (missing & (1 << k) && (fleet.used[k] ?? 0) >= (fleet.total[k] ?? 0)) return false;
  }
  for (let k = 0; k < GSE_COUNT; k++) {
    if (!(missing & (1 << k))) continue;
    fleet.used[k] = (fleet.used[k] ?? 0) + 1;
    if (a.gseEta && travel) a.gseEta[k] = travel(k);
  }
  a.gse |= missing;
  return true;
}

/** Whether every vehicle in `mask` the aircraft holds has arrived at it. */
export function arrived(a: { gse: number; gseEta: readonly number[] }, mask: number): boolean {
  for (let k = 0; k < GSE_COUNT; k++) {
    if (mask & (1 << k) && a.gse & (1 << k) && (a.gseEta[k] ?? 0) > 0) return false;
  }
  return true;
}

/** Hands back every vehicle the aircraft holds outside `keep`. */
export function release(fleet: Fleet, a: { gse: number; gseEta?: number[] }, keep: number): void {
  const freed = a.gse & ~keep;
  for (let k = 0; k < GSE_COUNT; k++) {
    if (!(freed & (1 << k))) continue;
    fleet.used[k] = Math.max(0, (fleet.used[k] ?? 0) - 1);
    if (a.gseEta) a.gseEta[k] = 0;
  }
  a.gse &= keep;
}

/**
 * Ticks a ground vehicle takes per tile: 0.2 tiles a tick, about the pace of the road traffic
 * (a little brisker), so service vehicles and cars keep the same speed on the screen.
 */
export const GSE_TICKS_PER_TILE = 5;
/** On a service road (no other traffic, no lights): quicker, and so the way they take. */
export const SERVICE_TICKS_PER_TILE = 3;

export function travelTicks(tiles: number): number {
  return tiles <= 0 ? 0 : tiles * GSE_TICKS_PER_TILE;
}
